import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { translateService } from "../../services/TranslateService";
import styles from "./main.module.scss";
import React from "react";
import { GlobalStore } from "../../reduxStore";
import { cx } from "./util";
import { DataContext, ISearchParameters } from "../../contexts/dataContext";

export default function Coupon({ carId, setSearchParams }: any) {
  const dataContext = useContext(DataContext);
  const apiService = useSelector((state: GlobalStore) => state.chauffeurReducer.apiService);
  const coupon = useSelector((state: GlobalStore) => state.chauffeurReducer.coupon);
  const dispatch = useDispatch();
  const [couponDescription, setCouponDescription] = useState(coupon?.description ?? '');
  const [couponCode, setCouponCode] = useState(dataContext.user?.couponCode! ?? (coupon?.code ?? ''));

  useEffect(() => {
    let urlCodeValid = sessionStorage.getItem('urlCodeValid') ?? undefined;
    if(urlCodeValid === 'false') {
      setCouponDescription(translateService.translate('vehicleSearch.couponWasNotValidMessage'));
      sessionStorage.removeItem('urlCodeValid');
    }
  }, [])

  useEffect(() => {
    // if code cleared at payment screen, the window reloaded --> vehicle object may need refresh if url code valid
    // if vehicle coupon data null, pricing will not update even if coupon is applied
    if(carId && coupon?.valid && couponCode && !dataContext.vehicle?.pricingData?.coupon) {
      search((previousState: ISearchParameters) => ({ ...previousState, discount: couponCode }));
    }
  }, [carId, coupon?.valid, dataContext.vehicle?.pricingData.coupon])

  const handleInvalidCode = () => {
    if(dataContext.route && dataContext.route === "/makereservation") { // if changing coupon on payment screen
      // these methods allows services and payment methods to update correctly and quicker
      if (setSearchParams) {
        setSearchParams((previousState: ISearchParameters) => ({ ...previousState, discount: null }))
      } else {
        search((previousState: ISearchParameters) => ({ ...previousState, discount: null }));
      }
    } else { // searching with coupon on Vehicle Search screen
      // only search again if latest results from valid coupon before the invalid coupon was applied --> resets results
      if (dataContext.searchParameters?.discount) {
        dataContext.changeSearchParameters({...dataContext.searchParameters, discount: null});
        // if no older coupon stored, removed invalid one without triggering reload
      }
    }
  }

  const removeDiscountCode = () => {
    setCouponCode('');
    setCouponDescription('');
    dataContext.changeUser({ ...dataContext.user, couponCode: '' });
    dispatch({ type: "SET_DISCOUNT_COUPON", payload: null });
    sessionStorage.removeItem("coupon");
    sessionStorage.setItem('urlCodeValid', 'removed');
    handleInvalidCode();
  }

  useEffect(() => {
    if(dataContext.searchParameters?.discount! && dataContext.searchParameters?.discount !== "" ){
      setCouponCode(dataContext.searchParameters?.discount! ?? '');
    }
  }, []);

  const search = (p: any) => {
    let newParams = p(dataContext.searchParameters);
    if (carId) {
      let mergeParams = { ...newParams, carId: carId };
      apiService?.vehicle.searchWithParams(mergeParams)
          .then(r => {
            if (r.count === 1) {
              if (r.vehicles[0].availabilityData.available) {
                dataContext.changeVehicle(r.vehicles[0])
              } else {
                dispatch({ type: 'SET_RESPONSE_MODAL', payload: translateService.translate("vehicleSearch1.notAvailable") })
                return;
              }
            }
            dataContext.changeSearchParameters(newParams);
          })
          .catch(e => {
            console.log(e);
            dispatch({ type: 'SET_RESPONSE_MODAL', payload: translateService.translate('vehicleSearch.errorLoadingResults') });
          })
          .finally(() =>
              dispatch({ type: 'SET_API_MESSAGE', payload: undefined })
          );
    } else {
      // this search will be a quicker process + easier on BE if no car id has been selected. Older method (above) does search twice, #todo - change to make more efficient
      dataContext.changeSearchParameters(newParams);
    }
  }

  function getCoupon() {
    apiService?.vehicle.getCoupon(couponCode)
      .then(r => {
        if (!r.valid) {
          setCouponDescription(translateService.translate('vehicleSearch.couponWasNotValidMessage'));
        }
        else {
          if (setSearchParams) {
            setSearchParams((previousState: ISearchParameters) => ({ ...previousState, discount: couponCode }))
          }
          else {
            search((previousState: ISearchParameters) => ({ ...previousState, discount: couponCode }));
            if (dataContext.user?.couponCode) //don't show user profile set coupon description - don't want
                // to remind driver of negative experience coupon sought to remedy
              setCouponDescription(translateService.translate('vehicleSearch.couponAutoApplied'));
            else {
              setCouponDescription(r.description);
            }
          }
          dataContext.changeValidatedVipVehicles(-1); // clear VIP codes if any car were previously unlocked (VIP not allowed with other discounts)
        }
        dispatch({ type: "SET_DISCOUNT_COUPON", payload: { ...r, code: couponCode } });
      }).catch(e => {
        console.log('coupon fail ' + e, e._errorMessage, e.errorMessage)
        if (e.includes("NoCouponWithProvidedCode")) {
          setCouponDescription(translateService.translate('vehicleSearch.couponWasNotValidMessage'));
        }
        else {
          setCouponDescription(translateService.translate('vehicleSearch.errorInfo'));
        }
        handleInvalidCode();
        dispatch({ type: "SET_DISCOUNT_COUPON", payload: null });

      })
  }

  useEffect(() => {
    const currentCouponCode = dataContext.user?.couponCode;
    if (currentCouponCode && currentCouponCode !== '') {
      setCouponCode(currentCouponCode);
      getCoupon();
    }
  }, [dataContext.user?.couponCode]);

  return <>
    <div className={styles.vehicleSearch}>
      {!dataContext.user?.couponCode && (
        <form onSubmit={e => e.preventDefault()}>
          <div className={cx(styles.r24FullWidthFlexCenter, styles.r24Padding10)} >
            <div className={cx(styles.r24ChildFlex1, styles.grayBorderAround)}>
              <input type="text"
                className={cx(styles.discountInput)}
                onChange={e => setCouponCode(e.target.value)}
                value={couponCode}
                id="discount"
                placeholder={translateService.translate('vehicleSearch.discountcode')}
              />
            </div>
            <div className={cx(styles.r24ChildFlex1, styles.r24LeftMargin5)}>
              <button disabled={!couponCode}
                className={"btn btn-primary btn-outline-primary w-100 font-weight-bold text-uppercase"}
                onClick={() => { getCoupon(); dataContext.sendGaEvent('searchOptions', 'discount', '', 0) }}
                type='submit'
              >
                {translateService.translate('vehicleSearch.getDiscount')}
              </button>
            </div>
          </div>
        </form>
      )}
      {couponDescription !== '' &&
        <div className={cx(styles.r24FullWidth, styles.r24TopMargin10, styles.r24BottomMargin20)}>
          <div className={cx(styles.r24FullWidth, styles.redBackground)}>
            {coupon?.valid ? // note: this is a hotfix --> somehow 'forwash' code came back valid: false but description is also getting set normally
                couponDescription : translateService.translate('vehicleSearch.couponWasNotValidMessage')}
          </div>
          <div style={{paddingTop: 5}}>
            <button className={"btn btn-primary btn-outline-primary w-100"}
                onClick={() => {removeDiscountCode()}}>
              {translateService.translate('vehicleSearch.removeDiscountCode')}
            </button>
          </div>
        </div>
      }
    </div>
  </>
}